// Most między aplikacją Android (Capacitor) a istniejącym kontraktem zdarzeń
// wtyczki Chrome: odpowiadamy na MC_EXT_GET_SESSION i ogłaszamy sesję przez
// MC_EXT_SESSION_RESULT dokładnie jak content script rozszerzenia — tylko
// ciasteczka czytamy z natywnego CookieManagera (plugin McSession), a nie
// z chrome.cookies. Dzięki temu App.jsx nie musi wiedzieć, skąd sesja pochodzi.
// W przeglądarce (poza apką) cały moduł jest wyłączony.

const getCapacitor = () => (typeof window !== 'undefined' ? window.Capacitor : undefined);

export const isNativeApp = () => {
  const cap = getCapacitor();
  return !!(cap && cap.isNativePlatform && cap.isNativePlatform());
};

const mcPlugin = () => getCapacitor()?.Plugins?.McSession;

// To samo dopasowanie kluczy co getMcSession() we wtyczce i _parse_mc_cookie_string
// w backendzie — sesja ma identyczny kształt niezależnie od źródła.
function parseCookieString(str) {
  if (!str) return null;
  const map = {};
  str.split(/;\s*/).forEach(part => {
    const i = part.indexOf('=');
    if (i > 0) map[part.slice(0, i)] = part.slice(i + 1);
  });
  const names = Object.keys(map);
  const wpKey = names.find(k => k.startsWith('wordpress_logged_in_'));
  const lidlKey = names.find(k =>
    k === 'lidl_sso_id_token' || k === 'lidl_sso_token' ||
    k.startsWith('lidl_sso') || k.startsWith('lidl_connect') || k.startsWith('lidl_')
  );
  if (!wpKey || !lidlKey) return null;
  return {
    all_cookies_str: str,
    php_session_id: map.PHPSESSID || '',
    wp_logged_in_key: wpKey,
    wp_logged_in_value: map[wpKey],
    lidl_sso_key: lidlKey,
    lidl_sso_token: map[lidlKey],
    gclb: map.GCLB || '',
  };
}

function announceSession(session) {
  document.dispatchEvent(new CustomEvent('MC_EXT_SESSION_RESULT', { detail: { session } }));
}

let lastRefreshAt = 0;

async function readAndAnnounce() {
  const plugin = mcPlugin();
  if (!plugin) return null;
  try {
    // Token SSO Lidla żyje ~5 min — zwykły odczyt ciasteczek z dysku bywa
    // przeterminowany. refresh() przeładowuje stronę MC w niewidocznym WebView
    // i odświeża token; throttle, żeby nie mielić sieci przy serii pingów.
    const useRefresh = Date.now() - lastRefreshAt > 45000;
    if (useRefresh) lastRefreshAt = Date.now();
    const { cookies } = useRefresh ? await plugin.refresh() : await plugin.getCookies();
    const session = parseCookieString(cookies);
    // Ogłaszamy ZAWSZE, nawet bez ważnej sesji (session=null) — App.jsx traktuje
    // samo dotarcie zdarzenia jako dowód, że moduł natywny żyje (extDetected),
    // dokładnie jak content.js wtyczki. applySession() ignoruje null bezpiecznie.
    announceSession(session);
    return session;
  } catch {
    announceSession(null);
    return null;
  }
}

let initialized = false;

export function initMcNative() {
  if (!isNativeApp() || initialized) return;
  initialized = true;
  document.addEventListener('MC_EXT_GET_SESSION', () => { readAndAnnounce(); });
  // Cykliczne odświeżanie trzyma token żywym, gdy apka jest długo otwarta —
  // applySession w App.jsx deduplikuje, więc identyczna sesja nic nie kosztuje.
  setInterval(readAndAnnounce, 4 * 60 * 1000);
}

// Otwiera natywne okno logowania MC; po sukcesie ogłasza sesję jak wtyczka.
export async function connectMcNative(lang, hint) {
  const plugin = mcPlugin();
  if (!plugin) return null;
  const mcLang = lang === 'de' ? 'de-de' : 'pl-pl';
  try {
    const { cookies } = await plugin.connect({ lang: mcLang, hint: hint || '' });
    lastRefreshAt = Date.now();
    const session = parseCookieString(cookies);
    if (session) announceSession(session);
    return session;
  } catch {
    return null;
  }
}
